import styles from './HeroModule.module.scss';

import React from 'react';

import type { HeroModule as HeroModuleType } from 'api/contentful/contentTypes.cloverdotcom';
import { RichBlurb } from 'components/RichBlurb/RichBlurb';
import { SubjectImage } from 'components/SubjectImage/SubjectImage';
import { TextV2 } from 'components/TextV2/TextV2';
import { VideoPlaybackButton } from 'components/VideoPlaybackButton/VideoPlaybackButton';
import { useVideoSrcFromSubject } from 'hooks/useVideoSrcFromSubject';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { useVideoInViewPlayback } from 'hooks/useVideoInViewPlayback';
import { FlatEntry } from 'services/cms/flattenContentful';
import { AppType } from 'services/cms/typesUtil';

type HeroModuleProps = AppType.Component<FlatEntry<HeroModuleType>>;

export const HeroModule: React.FC<HeroModuleProps> = ({ headingTagName = 'h2', item = {}, _attributes }) => {
  const shouldReduceMotion = useReducedMotion();
  const { inViewTargetRef, videoRef, setIsVideoManualControl } = useVideoInViewPlayback();
  const { posterSrc, videoSrc } = useVideoSrcFromSubject(item);
  const { title, subTitle, blurb, richBlurb } = item;

  return (
    <section className={styles.heroModule} ref={inViewTargetRef} {..._attributes}>
      {videoSrc && !shouldReduceMotion ? (
        <video
          autoPlay
          className={styles.video}
          loop
          muted
          playsInline
          poster={posterSrc}
          ref={videoRef}
          role="presentation"
          src={videoSrc}
        />
      ) : (
        <SubjectImage className={styles.image} entry={item} />
      )}
      <span className={styles.bg} />
      <div className={styles.container}>
        <div className={styles.text}>
          <TextV2 className={styles.subTitle} value={subTitle} variant="modern-eyebrow" />
          <TextV2 className={styles.title} value={title} tagName={headingTagName} variant="modern-h1" />
          <TextV2 className={styles.blurb} value={blurb} variant="modern-body-large" isMarkdown />
          <RichBlurb
            noParagraph
            className={styles.blurb}
            document={richBlurb}
            textVariant="modern-body-large"
          />
        </div>
      </div>
      <VideoPlaybackButton
        videoRef={videoRef}
        className={styles.videoPlaybackButton}
        setIsVideoManualControl={setIsVideoManualControl}
      />
    </section>
  );
};
